// MVP: in-memory + localStorage persistence for Dizee Links (no backend required for demo)

import type { DizeeLink, LinkSubmission } from '../types/links'
import { getPrimaryArtistId } from './api'

const LINKS_KEY = 'dizee_links'
const SUBMISSIONS_KEY = 'dizee_link_submissions'

function storageKey(userId: string): string {
  return `${LINKS_KEY}:${userId}`
}

function submissionsKey(userId: string): string {
  return `${SUBMISSIONS_KEY}:${userId}`
}

export function getEffectiveUserId(): string | null {
  const primary = getPrimaryArtistId()
  if (primary) return primary
  try {
    const userRaw = localStorage.getItem('dizeeUser')
    if (userRaw) {
      const user = JSON.parse(userRaw)
      return user._id ?? null
    }
  } catch {
    // ignore
  }
  return null
}

export function loadLinks(userId: string): DizeeLink[] {
  try {
    const raw = localStorage.getItem(storageKey(userId))
    if (!raw) return []
    const data = JSON.parse(raw)
    return Array.isArray(data) ? data : []
  } catch {
    return []
  }
}

export function saveLinks(userId: string, links: DizeeLink[]): void {
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify(links))
  } catch {
    // ignore
  }
}

export function addLink(userId: string, link: DizeeLink): void {
  const links = loadLinks(userId)
  if (links.some((l) => l.linkId === link.linkId)) return
  links.push(link)
  saveLinks(userId, links)
}

function mergeLinkConfig(
  prev: Record<string, unknown> | undefined,
  next: Record<string, unknown> | undefined,
): Record<string, unknown> | undefined {
  if (!next) return prev
  const base = prev && typeof prev === 'object' ? { ...prev } : {}
  const merged: Record<string, unknown> = { ...base, ...next }
  const pUrls = base.platformUrls
  const nUrls = next.platformUrls
  if (pUrls && typeof pUrls === 'object' && nUrls && typeof nUrls === 'object') {
    merged.platformUrls = { ...(pUrls as object), ...(nUrls as object) }
  }
  return merged
}

export function updateLink(userId: string, linkId: string, patch: Partial<DizeeLink>): void {
  const links = loadLinks(userId)
  const idx = links.findIndex((l) => l.linkId === linkId)
  if (idx === -1) return
  const prev = links[idx]
  if (patch.config !== undefined) {
    const prevRec =
      prev.config && typeof prev.config === 'object' ? (prev.config as Record<string, unknown>) : undefined
    const merged = mergeLinkConfig(prevRec, patch.config as Record<string, unknown>)
    links[idx] = { ...prev, ...patch, config: merged }
  } else {
    links[idx] = { ...prev, ...patch }
  }
  saveLinks(userId, links)
}

export function getLinkById(userId: string, linkId: string): DizeeLink | null {
  return loadLinks(userId).find((l) => l.linkId === linkId) ?? null
}

export function getLinkBySlug(userId: string, slug: string): DizeeLink | null {
  return loadLinks(userId).find((l) => l.slug === slug) ?? null
}

/** Find link by linkId (e.g. for collect URL). Scans all users' links. */
export function getLinkByIdGlobal(linkId: string): { link: DizeeLink; userId: string } | null {
  const prefix = LINKS_KEY + ':'
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(prefix)) continue
    const userId = key.slice(prefix.length)
    try {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const links: DizeeLink[] = JSON.parse(raw)
      const link = links.find((l) => l.linkId === linkId)
      if (link) return { link, userId }
    } catch {
      // skip
    }
  }
  return null
}

/** Find link by public path + slug (e.g. release/misbehave). Scans all users. */
export function getLinkByPathAndSlug(path: 'collect' | 'release', slug: string): { link: DizeeLink; userId: string } | null {
  const prefix = LINKS_KEY + ':'
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(prefix)) continue
    const userId = key.slice(prefix.length)
    try {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const links: DizeeLink[] = JSON.parse(raw)
      const link = links.find((l) => (l.slug === slug && (path === 'release' ? l.linkType === 'release' : l.linkType === 'data_collection')))
      if (link) return { link, userId }
    } catch {
      // skip
    }
  }
  return null
}

export function recordClick(linkId: string): void {
  const found = getLinkByIdGlobal(linkId)
  if (found) {
    updateLink(found.userId, linkId, { clickCount: found.link.clickCount + 1 })
  }
}

export function getClickCount(linkId: string): number {
  const found = getLinkByIdGlobal(linkId)
  return found ? found.link.clickCount : 0
}

export function loadSubmissions(userId: string, linkId?: string): LinkSubmission[] {
  try {
    const raw = localStorage.getItem(submissionsKey(userId))
    if (!raw) return []
    const data = JSON.parse(raw)
    const list = Array.isArray(data) ? data : []
    if (linkId) return list.filter((s: LinkSubmission) => s.linkId === linkId)
    return list
  } catch {
    return []
  }
}

export function addSubmission(userId: string, submission: LinkSubmission): void {
  try {
    const key = submissionsKey(userId)
    const list = loadSubmissions(userId)
    list.push(submission)
    localStorage.setItem(key, JSON.stringify(list))
  } catch {
    // ignore
  }
}

export function removeLink(userId: string, linkId: string): void {
  const links = loadLinks(userId)
  const filtered = links.filter((l) => l.linkId !== linkId)
  if (filtered.length !== links.length) {
    saveLinks(userId, filtered)
  }
}

/** Check whether a release slug is available across all users. */
export function isSlugAvailableGlobally(slug: string, excludeLinkId?: string): boolean {
  const prefix = LINKS_KEY + ':'
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i)
    if (!key?.startsWith(prefix)) continue
    try {
      const raw = localStorage.getItem(key)
      if (!raw) continue
      const links: DizeeLink[] = JSON.parse(raw)
      const conflict = links.find((l) => l.slug === slug && l.linkType === 'release' && l.linkId !== excludeLinkId)
      if (conflict) return false
    } catch {
      // skip
    }
  }
  return true
}

export function generateLinkId(): string {
  return 'l_' + Math.random().toString(36).slice(2, 12)
}

export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'link-' + Math.random().toString(36).slice(2, 8)
}
